import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals/constants';
import {
  type LedgerDrawerProps,
  type PaymentSheetRequest,
  useOpenLedgerDrawer,
} from '@features/patient-ledger';
import { openLedgerDrawer } from '@features/patient-ledger/store/slice';
import { useUserState } from '@features/user';
import { usePatientById } from '@hooks/usePatientById';
import {
  FeatureGates,
  useEventTracking,
  useFeatureGate,
  useSessionReplayCapture,
} from '@services/statsig';
import { useModal } from '@store/hooks/useModal';

import { useStripeConnect } from '../store/hooks';
import type { PatientPaymentMethod } from '../types';

export interface RequestPaymentOptions {
  amount?: string;
  patientId?: string;
  patientName?: string;
  reason?: string;
  dueDate?: string;
  appointmentId?: string;
  encounterId?: string;
  actionId?: string;
  source?: string;
  patientTelephone?: string;
  hasPaymentMethod?: boolean;
  defaultPaymentMethod?: PatientPaymentMethod;
  paymentMethods?: PatientPaymentMethod[];
  onSuccess?: () => void;
}

interface RequestPaymentState {
  isLoading: boolean;
  canRequest: boolean;
  triggerPaymentRequest: () => void;
}

/**
 * Headless hook for requesting payments from patients.
 *
 * Pre-flight: SMS onboarding + Stripe Connect must both be set up. Once
 * those are clear, opens the adhoc Charge composer — the settle sheet with
 * method=card_on_file when a card exists, else invoice, uncovered lines
 * opt-in.
 *
 * Gated (feature_patient_balance_table) that composer is the payment sheet
 * modal; ungated it is the Patient Ledger Drawer. Either way it is the same
 * surface the patient billing panel and encounter detail open, so "Request
 * payment" lands in the user's existing mental model (gdn-2957).
 *
 * Several legacy options (defaultPaymentMethod, paymentMethods,
 * patientTelephone, dueDate, actionId) are dropped. The settle sheet
 * collects mobile override + reason itself, and uses the patient's default
 * card when method=card_on_file.
 */
export function useRequestPayment(options: RequestPaymentOptions = {}): RequestPaymentState {
  const {
    amount,
    patientId,
    patientName,
    encounterId,
    source = 'unknown',
    patientTelephone,
    hasPaymentMethod,
  } = options;

  const { open: showModal, openAsDestination } = useModal();
  const openDrawer = useOpenLedgerDrawer();
  // gdn-2957: gated, the Charge composer is a page modal rather than the
  // ledger drawer. Ungated users keep the drawer — it is still their only
  // surface for this.
  const useSheet = useFeatureGate(FeatureGates.PATIENT_BALANCE_TABLE);
  const { hasStripeAccount } = useStripeConnect();
  const { startCapture } = useSessionReplayCapture();
  const { payment, smsOnboarding, stripeConnect } = useEventTracking();
  const userState = useUserState();
  const location = useLocation();
  const navigate = useNavigate();
  // TODO: when this gets moved to a query hook, we can skip if telephone and name are provided
  const { patient } = usePatientById(patientId);
  const patientNameFinal =
    patientName || (patient ? `${patient.first_name} ${patient.last_name}` : undefined);

  const isSMSOnboarded = userState?.onboardingSteps.patient_communication_sms_onboarding_completed;
  const canRequest = true;

  // Convert legacy `amount` (dollars-as-string) into cents for the drawer.
  const defaultAmountCents = amount ? Math.round(parseFloat(amount) * 100) : undefined;

  const buildLedgerProps = (): LedgerDrawerProps => ({
    patientId: patientId ?? '',
    patientName: patientNameFinal ?? null,
    encounterId,
    defaultAmountCents,
    initialTab: 'ledger',
    // Adhoc-first Charge composer: card on file is the default when one
    // exists (the toolbar label literally says "Charge"), and uncovered
    // charge lines start unselected — the user opts them into the invoice.
    initialAction: {
      kind: 'settle',
      method: hasPaymentMethod ? 'card_on_file' : 'invoice',
      preselectLines: false,
    },
    source,
  });

  const buildSheetRequest = (): PaymentSheetRequest => ({
    patientId: patientId ?? '',
    patientName: patientNameFinal ?? null,
    encounterId,
    defaultAmountCents,
    action: {
      kind: 'settle',
      method: hasPaymentMethod ? 'card_on_file' : 'invoice',
      preselectLines: false,
    },
    source,
  });

  /** Open whichever payment surface this user is on. */
  const openPaymentSurface = () => {
    if (useSheet) {
      // Destination semantics: re-triggering re-seeds the sheet rather
      // than stacking another dialog behind the first.
      openAsDestination(MODAL_TYPES.PAYMENT_SHEET, buildSheetRequest());
      return;
    }
    openDrawer(buildLedgerProps());
  };

  /**
   * The same choice, as a serialized Redux action — the SMS onboarding
   * modal dispatches this once it finishes. The sheet's form is an
   * ordinary modal push, which is the shape the Stripe Connect
   * continuation beside it already uses.
   */
  const buildContinuationAction = () =>
    useSheet
      ? {
          type: 'modals/replaceModal',
          payload: {
            modalType: MODAL_TYPES.PAYMENT_SHEET,
            modalProps: buildSheetRequest(),
          },
        }
      : {
          type: openLedgerDrawer.type,
          payload: buildLedgerProps(),
        };

  const triggerPaymentRequest = useCallback(() => {
    startCapture('payment_request_triggered');
    payment.requestInitiated({
      source,
      amount: amount ? parseFloat(amount) : undefined,
    });

    if (!isSMSOnboarded) {
      smsOnboarding.initiated({ source: 'payment_request' });
      showModal(MODAL_TYPES.PATIENT_COMMUNICATION_ONBOARDING, {
        // After SMS onboarding completes, either continue into Stripe
        // Connect (still a real modal) or fire the slice action to open
        // the ledger drawer. The continuation is dispatched as a Redux
        // action by the onboarding modal — slice action types match
        // their dispatched form so this works without a wrapper.
        continuationAction: !hasStripeAccount
          ? {
              type: 'modals/pushModal',
              payload: {
                modalType: MODAL_TYPES.STRIPE_CONNECT_INFO,
                modalProps: {
                  returnUrl: `${window.location.href}${window.location.search ? '&' : '?'}openPaymentRequest=true`,
                },
              },
            }
          : buildContinuationAction(),
      });
      return;
    }

    if (!hasStripeAccount) {
      stripeConnect.initiated({ source: 'payment_request' });
      showModal(MODAL_TYPES.STRIPE_CONNECT_INFO, {
        returnUrl: `${window.location.href}${window.location.search ? '&' : '?'}openPaymentRequest=true`,
      });
      return;
    }

    payment.modalOpened({ source });
    openPaymentSurface();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSMSOnboarded,
    hasStripeAccount,
    amount,
    patientId,
    patientNameFinal,
    encounterId,
    source,
    showModal,
    openDrawer,
    startCapture,
    payment,
    smsOnboarding,
    stripeConnect,
  ]);

  // Auto-open after Stripe Connect onboarding redirect.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpenPaymentRequest = params.get('openPaymentRequest') === 'true';

    if (shouldOpenPaymentRequest && hasStripeAccount && isSMSOnboarded) {
      stripeConnect.completed({});
      payment.modalOpened({ source: 'stripe_return' });
      openPaymentSurface();

      params.delete('openPaymentRequest');
      const newSearch = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
          hash: location.hash,
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigate, openDrawer, hasStripeAccount, isSMSOnboarded]);

  // Suppress unused-warning on legacy optional props that no longer affect behavior.
  void patientTelephone;

  return {
    isLoading: false,
    canRequest,
    triggerPaymentRequest,
  };
}
